/**
 * PATH: packages/email-hub/src/feature/reader-header/notices/noticeRegistry.ts
 * FILE_ID: EMAILHUB-READERHEADER-NOTICES-REGISTRY-001
 * PURPOSE: One severity-sorted definition per notice family for the Notice Center.
 * ROLE IN ARCHITECTURE: Pure domain registry between provider/ping/calendar state and UI.
 * MAIN EXPORTS: READER_NOTICE_REGISTRY, createReaderNotice, sortReaderNotices.
 * NON-RESPONSIBILITIES: This file does not render notices or run retries.
 * NOTES FOR FUTURE AI: Severity order is a product contract (plan D269-16); add kinds with an explicit rank.
 */
import type {
  ReaderNotice,
  ReaderNoticeCollapsePolicy,
  ReaderNoticeKind,
  ReaderNoticeLifecycle,
} from '../model/types'

export interface ReaderNoticeDefinition {
  kind: ReaderNoticeKind
  /**
   * Strict severity rank (higher wins the single at-rest notice slot):
   * detail/body failure > provider writeback failure > pending ping >
   * calendar invitation > attachment hydration > informational.
   */
  severity: number
  /** Blocking notices render inline before the body and never collapse into `+N notices`. */
  blocking: boolean
  defaultLifecycle: ReaderNoticeLifecycle
  collapsePolicy: ReaderNoticeCollapsePolicy
  /** Stable action ids this notice family may expose (plan "Notice Center actions"). */
  actionSet: readonly string[]
}

export const READER_NOTICE_REGISTRY: Readonly<
  Record<ReaderNoticeKind, ReaderNoticeDefinition>
> = {
  error: {
    kind: 'error',
    severity: 50,
    blocking: true,
    defaultLifecycle: 'blocking',
    collapsePolicy: 'on-resolve',
    actionSet: ['retry'],
  },
  provider: {
    kind: 'provider',
    severity: 40,
    blocking: false,
    defaultLifecycle: 'active',
    collapsePolicy: 'on-resolve',
    actionSet: ['retry', 'restore_provider_state', 'view_error_detail'],
  },
  ping: {
    kind: 'ping',
    severity: 30,
    blocking: false,
    defaultLifecycle: 'pending',
    collapsePolicy: 'on-acknowledge',
    actionSet: ['open_linked_work', 'mark_viewed', 'dismiss', 'reply_to_sender'],
  },
  calendar: {
    kind: 'calendar',
    severity: 20,
    blocking: false,
    defaultLifecycle: 'pending',
    collapsePolicy: 'on-acknowledge',
    actionSet: ['view_event', 'accept', 'tentative', 'decline', 'open_calendar'],
  },
  attachments: {
    kind: 'attachments',
    severity: 10,
    blocking: false,
    defaultLifecycle: 'pending',
    collapsePolicy: 'on-resolve',
    actionSet: ['retry'],
  },
  info: {
    kind: 'info',
    severity: 0,
    blocking: false,
    defaultLifecycle: 'active',
    collapsePolicy: 'auto-after-acknowledge',
    actionSet: ['dismiss'],
  },
}

export interface CreateReaderNoticeInput {
  id: string
  kind: ReaderNoticeKind
  label: string
  lifecycle?: ReaderNoticeLifecycle
  actionLabel?: string
}

/**
 * Stamp a notice with its registry-owned severity, collapse policy, and
 * action set so producers cannot invent ad-hoc ordering.
 *
 * Inputs: id/kind/label plus optional lifecycle override. Output: a complete
 * `ReaderNotice`. Side effects: none. Unknown kinds are impossible by type.
 */
export function createReaderNotice({
  id,
  kind,
  label,
  lifecycle,
  actionLabel,
}: CreateReaderNoticeInput): ReaderNotice {
  const definition = READER_NOTICE_REGISTRY[kind]
  return {
    id,
    kind,
    severity: definition.severity,
    lifecycle: lifecycle ?? definition.defaultLifecycle,
    collapsePolicy: definition.collapsePolicy,
    label,
    actions: definition.actionSet,
    actionLabel,
  }
}

/** Highest severity first; stable id tiebreak so renders never reshuffle. */
export function sortReaderNotices(
  notices: readonly ReaderNotice[],
): ReaderNotice[] {
  return [...notices].sort(
    (left, right) =>
      right.severity - left.severity || left.id.localeCompare(right.id),
  )
}

/** Lifecycles that still demand the at-rest slot; the rest fold into "+N". */
const AT_REST_LIFECYCLES = new Set<ReaderNoticeLifecycle>([
  'blocking',
  'active',
  'pending',
])

/**
 * Every notice still occupying the at-rest slot, severity-sorted. Shared by
 * `selectLeadAtRestNotice` (the [0] element) and NoticeSummary's "+N" count
 * (`length - 1`) so a resolved/acknowledged notice can never inflate either
 * number — ONE filtered list, two derived readings.
 */
export function selectAtRestNotices(
  notices: readonly ReaderNotice[],
): ReaderNotice[] {
  return sortReaderNotices(notices).filter((notice) =>
    AT_REST_LIFECYCLES.has(notice.lifecycle),
  )
}

/**
 * The single highest-severity notice occupying the at-rest slot, or null.
 * Shared by NoticeSummary's render and the header's sr-only live region
 * (WORK ITEM 11) so both agree on exactly one definition of "the current
 * notice."
 */
export function selectLeadAtRestNotice(
  notices: readonly ReaderNotice[],
): ReaderNotice | null {
  return selectAtRestNotices(notices)[0] ?? null
}
